<div class="min-h-screen bg-base-200 p-4">
  <div class="max-w-4xl mx-auto">
    <!-- Loading State -->
    @if (isLoading) {
      <div data-testid="profile-loading" class="flex justify-center items-center py-16">
        <app-icon name="refresh" class="animate-spin mr-3"></app-icon>
        <span class="text-lg text-base-content">Loading profile...</span>
      </div>
    }

    <!-- Error State -->
    @if (!isLoading && errorMessage) {
      <div data-testid="profile-error-alert" class="alert alert-error">
        <app-icon name="error"></app-icon>
        <span>{{ errorMessage }}</span>
      </div>
    }

    <!-- Success State -->
    @if (!isLoading && successMessage) {
      <div data-testid="profile-success-alert" class="alert alert-success mb-4" role="status" aria-live="polite">
        <app-icon name="check_circle"></app-icon>
        <span>{{ successMessage }}</span>
      </div>
    }

    <!-- KAN-562: shown only when the course-player gate sent the learner here. -->
    <app-profile-gate-banner
      [returnUrl]="gateReturnUrl"
      [isComplete]="requestedFieldsComplete"></app-profile-gate-banner>

    <!-- Profile Content -->
    @if (!isLoading && !errorMessage && user) {
      <div data-testid="profile-content-section" [attr.data-ready]="!isLoading" class="space-y-6">
        <!-- Profile Header -->
        <div class="card bg-base-100 shadow-xl">
          <div class="card-body">
            <div class="flex items-start justify-between flex-wrap gap-4">
              <!-- KAN-616: below sm the avatar stacks above the name/email so the text
                   uses the full card width; the name and email wrap (2-line clamp)
                   instead of single-line ellipsis. sm and up keep the side-by-side layout. -->
              <div class="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4 min-w-0 grow">
                <div class="avatar placeholder flex-shrink-0">
                  <div class="bg-primary text-primary-content rounded-full w-20 h-20">
                    <span class="text-2xl font-bold">{{ (user.name || user.email || '?')[0].toUpperCase() }}</span>
                  </div>
                </div>
                <div class="min-w-0 w-full sm:w-auto sm:flex-1">
                  <h1 data-testid="profile-name-heading" class="text-2xl font-bold text-base-content user-text-clamp-2" [title]="user.name || 'User Profile'">{{ user.name || 'User Profile' }}</h1>
                  <p class="text-base-content/70 user-text-clamp-2" [title]="user.email">{{ user.email }}</p>
                  @if (user.isSystemAdmin) {
                    <div class="flex items-center mt-2 space-x-2">
                      <app-icon name="verified_user" class="text-success"></app-icon>
                      <span class="text-sm font-medium text-success">System Administrator</span>
                    </div>
                  }
                </div>
              </div>
              <a routerLink="/profile/edit" data-testid="profile-edit-link" class="btn btn-primary">
                <app-icon name="edit" class="mr-2"></app-icon>
                Edit Profile
              </a>
            </div>
          </div>
        </div>
        <!-- Account Information -->
        <div class="card bg-base-100 shadow-xl">
          <div class="card-body">
            <h2 class="card-title text-base-content">
              <app-icon name="account_circle" class="mr-2"></app-icon>
              Account Information
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label class="text-sm font-medium text-base-content/70">User ID</label>
                <p class="mt-1 text-base-content font-mono text-sm">{{ user.id }}</p>
              </div>
              <div>
                <label class="text-sm font-medium text-base-content/70">Account Status</label>
                <p class="mt-1">
                  <span class="badge"
                    data-testid="profile-status-badge"
                    [attr.data-teststate]="user.status"
                      [ngClass]="{
                        'badge-success': user.status === 'active',
                        'badge-error': user.status === 'inactive',
                        'badge-warning': user.status === 'suspended'
                      }">
                    {{ user.status || 'active' }}
                  </span>
                </p>
              </div>
              <div>
                <label class="text-sm font-medium text-base-content/70">Member Since</label>
                <p class="mt-1 text-base-content">
                  {{ user.createdAt ? (user.createdAt | date:'longDate') : 'N/A' }}
                </p>
              </div>
              <div>
                <label class="text-sm font-medium text-base-content/70">Last Activity</label>
                <p class="mt-1 text-base-content">
                  {{ user.lastLoginAt ? (user.lastLoginAt | date:'short') : 'N/A' }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <!-- KAN-468: org profile fields (standard seeded set + instructor customs), learner self-edit -->
        <app-profile-fields
          [hasOrganization]="organizations.length > 0"
          (requestedCompleteChange)="onRequestedCompleteChange($event)"></app-profile-fields>
        <!-- Unit-completion notifications are sent to instructors/admins only. -->
        @if (canReceiveInstructorNotifications) {
          <app-email-notification-settings></app-email-notification-settings>
        }
        <!-- Organization Affiliations -->
        <div class="card bg-base-100 shadow-xl">
          <div class="card-body">
            <h2 class="card-title text-base-content">
              <app-icon name="business" class="mr-2"></app-icon>
              Organization Affiliations
            </h2>
            @if (organizations.length === 0) {
              <div data-testid="profile-orgs-empty" class="text-center py-8 text-base-content/50">
                <app-icon name="business_center" class="text-4xl mb-2"></app-icon>
                <p>You are not affiliated with any organizations.</p>
              </div>
            }
            @if (organizations.length > 0) {
              <div data-testid="profile-orgs-list" class="space-y-3 mt-4">
                @for (org of organizations; track org) {
                  <div
                    [attr.data-testid]="'profile-org-' + org.id + '-item'"
                    class="border border-base-300 rounded-lg p-4 bg-base-200">
                    <!-- KAN-715: below sm the name/role block takes the full row and the
                         buttons sit under it; the name wraps at word boundaries instead of
                         a single-line ellipsis. sm and up keep the buttons beside the name. -->
                    <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div class="min-w-0 w-full sm:w-auto sm:flex-1">
                        <h3 class="font-medium text-base-content flex items-center gap-2 flex-wrap min-w-0">
                          <span class="user-text-wrap min-w-0" [title]="org.name">{{ org.name }}</span>
                          @if (org.isDefault) {
                            <span data-testid="profile-org-default-badge"
                              class="badge badge-sm badge-primary badge-outline flex-shrink-0">Default</span>
                          }
                          @if (isCurrentOrganization(org.id) && !org.isDefault) {
                            <span class="badge badge-sm badge-ghost flex-shrink-0">Active</span>
                          }
                        </h3>
                        <p class="text-sm text-base-content/70 mt-1">
                          Role: <span class="font-medium">{{ getOrganizationRole(org) }}</span>
                        </p>
                      </div>
                      <div class="flex flex-wrap items-center gap-2 sm:flex-shrink-0">
                        @if (!org.isDefault && organizations.length > 1) {
                          <button
                            [attr.data-testid]="'profile-org-' + org.id + '-set-default-btn'"
                            [attr.aria-label]="'Set ' + org.name + ' as default organization'"
                            class="btn btn-outline btn-primary btn-sm whitespace-nowrap"
                            [disabled]="settingDefaultOrgId !== null"
                            [attr.aria-busy]="isSettingDefault(org.id)"
                            (click)="setDefaultOrganization(org)">
                            @if (isSettingDefault(org.id)) {
                              <span class="loading loading-spinner loading-xs"></span>
                              Setting...
                            } @else {
                              Set as Default
                            }
                          </button>
                        }
                        <button
                          [attr.data-testid]="'profile-org-' + org.id + '-leave-btn'"
                          [attr.aria-label]="'Leave ' + org.name"
                          class="btn btn-outline btn-error btn-sm whitespace-nowrap"
                          [disabled]="leavingOrgId !== null || settingDefaultOrgId !== null"
                          (click)="openLeaveModal(org)">
                          @if (leavingOrgId === org.id) {
                            <span class="loading loading-spinner loading-xs"></span>
                            Leaving...
                          } @else {
                            Leave
                          }
                        </button>
                      </div>
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        </div>
        <!-- Leave Organization Modal -->
        @if (showLeaveModal && orgToLeave) {
          <div class="modal modal-open" role="dialog" aria-modal="true" aria-labelledby="leave-org-title">
            <div class="modal-box">
              <h3 id="leave-org-title" class="font-bold text-lg text-warning mb-4">Leave Organization</h3>
              <p class="mb-4 user-text-wrap">Are you sure you want to leave <strong>{{ orgToLeave.name }}</strong>?</p>

              <div class="alert alert-warning mb-4" role="alert">
                <app-icon name="warning"></app-icon>
                <span>Your enrollment history and progress will be preserved, but you will lose access to this organization's courses and content.</span>
              </div>

              @if (organizations.length === 1) {
                <div class="alert alert-error mb-4" role="alert">
                  <app-icon name="error"></app-icon>
                  <span>This is your only organization. After leaving, you will need a new invitation to access any content.</span>
                </div>
              }

              @if (leaveErrorMessage) {
                <div class="alert alert-error mb-4" role="alert" data-testid="leave-org-error">
                  <app-icon name="error"></app-icon>
                  <span>{{ leaveErrorMessage }}</span>
                </div>
              }

              <div class="modal-action">
                <button class="btn btn-ghost" (click)="cancelLeave()" [disabled]="leavingOrgId !== null">Cancel</button>
                <button class="btn btn-warning" data-testid="leave-org-confirm-btn" (click)="confirmLeave()" [disabled]="leavingOrgId !== null">
                  @if (leavingOrgId) {
                    <span class="loading loading-spinner loading-sm mr-2"></span>
                    Leaving...
                  } @else {
                    Leave Organization
                  }
                </button>
              </div>
            </div>
            <label class="modal-backdrop" (click)="cancelLeave()"></label>
          </div>
        }

        <!-- Theme -->
        <div class="card bg-base-100 shadow-xl">
          <div class="card-body">
            <h2 class="card-title text-base-content">
              <app-icon name="palette" class="mr-2"></app-icon>
              Theme
            </h2>
            <app-theme-switcher></app-theme-switcher>
          </div>
        </div>
      </div>
    }
  </div>
</div>
